import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Settings as SettingsIcon } from 'lucide-react';
import StreakBadge from './StreakBadge';

/**
 * Slim signed-in top bar. Replaces the marketing <Header /> inside app shells
 * (/my-plan, /settings) so the dark-tactical UI isn't fighting the light-blue
 * marketing header. Keeps thumb-zone chrome to a minimum (~48px).
 *
 * Props:
 *   title         — page/tab title (e.g. "Today", "Training")
 *   eyebrow       — optional small caps label above the title
 *   back          — path to render a "back" chevron (e.g. "/my-plan")
 *   showStreak    — render StreakBadge in the right cluster
 *   showSettings  — render settings gear (link to /settings)
 *   rightSlot     — custom right-side content (overrides streak/settings)
 */
export default function TitleBar({
  title,
  eyebrow,
  back,
  showStreak = true,
  showSettings = true,
  rightSlot,
}) {
  return (
    <header
      className="sticky top-0 z-40 bg-[color:var(--fff-glass-bg)] backdrop-blur-xl border-b border-[color:var(--fff-glass-border)]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="app-titlebar"
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8 flex items-center gap-3 h-12 lg:h-14">
        {back ? (
          <Link
            to={back}
            aria-label="Back"
            data-testid="titlebar-back"
            className="shrink-0 -ml-1 h-9 w-9 rounded-full grid place-items-center text-zinc-300 hover:text-white hover:bg-white/5"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          </Link>
        ) : (
          <Link
            to="/"
            data-testid="titlebar-logo"
            className="shrink-0 text-white font-black tracking-tight text-lg leading-none select-none"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Free<span className="text-[#BEFF38]">Fit</span>Fuel
          </Link>
        )}

        <div className="flex-1 min-w-0 text-center">
          {eyebrow && (
            <p className="text-[9px] font-mono uppercase tracking-[0.22em] text-[#BEFF38] leading-none">
              {eyebrow}
            </p>
          )}
          {title && (
            <p
              className="text-white font-bold uppercase tracking-wide text-sm lg:text-base leading-tight mt-0.5 truncate"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="titlebar-title"
            >
              {title}
            </p>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-2 justify-end min-w-[3rem]">
          {rightSlot ? (
            rightSlot
          ) : (
            <>
              {showStreak && <StreakBadge pingOnMount />}
              {showSettings && (
                <Link
                  to="/settings"
                  aria-label="Settings"
                  data-testid="titlebar-settings"
                  className="h-9 w-9 rounded-full grid place-items-center text-zinc-400 hover:text-white hover:bg-white/5"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
